import {
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_SAVED_SEARCHES,
} from '@iconik-io/iconik-api/src/types/object_types';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import {
  ACLS_APPLY_TO_CONTENT,
  ACLS_DELETE_FROM_CONTENT,
} from '../../constants/modals';
import {
  CONTEXT_MENU_ACLS_LIST,
  connectContextMenuConsumer,
} from '../../contexts/context_menu';
import {
  ACL_TYPE_INHERITED,
  ACL_TYPE_OWN,
  aclPrefixes,
} from '../../ducks/common_acls/constants';
import { getCommonAclsSlice } from '../../ducks/common_acls/slice';
import { deleteEntityAcls } from '../../ducks/common_acls/thunks';
import type { CommonAclsPrefix } from '../../ducks/common_acls/types';
import { historyPush } from '../../ducks/history/actions';
import { openModal } from '../../ducks/modals_manager/slice';
import { ICONS } from '../../icons/icon';
import type { AppDispatch, AppState } from '../../store/types';
import { defaultPermissions } from '../../types/common';
import type { AclsContextItem } from './types';
import { getProfileIdFromAclsContextItem } from './utils';

const findAclsType = (
  state: AppState,
  id: string,
): CommonAclsPrefix | undefined =>
  aclPrefixes.find((type) => state.acls[type]?.entityCurrentUserAcls[id]);

export const getDefinition: GetDefinition<AclsContextItem> = (props, state) => {
  const { contextItems } = props;
  const [contextItem] = contextItems;
  const items: ContextMenuItemDefinition[] = [];
  if (!contextItem) {
    return { items };
  }

  let aclsType = contextItem.aclsType;
  const aclsObjectId = contextItem.aclsObjectId;
  let permissions = defaultPermissions;

  if (aclsObjectId) {
    for (const prefix of aclPrefixes) {
      const parentPermissions =
        state.acls[prefix]?.entityCurrentUserAcls[aclsObjectId];
      if (parentPermissions) {
        permissions = parentPermissions;
        aclsType = prefix;
      }
    }
  }

  if (!aclsObjectId) {
    return { items };
  }

  const type = findAclsType(state, aclsObjectId);

  if (contextItem.type === ACL_TYPE_INHERITED) {
    return {
      items: [
        {
          label: 'Show Source of Permission',
          action: () => (dispatch: AppDispatch) => {
            if (contextItem.objectType === OBJECT_TYPE_COLLECTIONS) {
              dispatch(historyPush(`/collection/${contextItem.objectKey}`));
            }
          },
          icon: ICONS.Open,
        },
      ],
    };
  }

  const profileId = getProfileIdFromAclsContextItem(contextItem);

  if (!profileId) {
    return { items };
  }

  const editAction: ContextMenuItemDefinition = {
    label: 'Edit Permission',
    action: () => (dispatch: AppDispatch) => {
      if (type) {
        dispatch(getCommonAclsSlice(type).actions.aclsSetEdit(contextItem.id));
      }
    },
    icon: ICONS.Edit,
  };

  const editContentAction: ContextMenuItemDefinition = {
    label: 'Permanently Apply to Content…',
    action: () => (dispatch: AppDispatch) => {
      dispatch(openModal({ id: ACLS_APPLY_TO_CONTENT, contextItems }));
    },
    icon: ICONS.Copy,
  };

  const deleteAction: ContextMenuItemDefinition = {
    label: 'Remove Permission',
    action: () => (dispatch: AppDispatch) => {
      if (type && profileId) {
        dispatch(
          deleteEntityAcls(type, aclsObjectId, profileId, contextItem.type),
        );
      }
    },
    icon: ICONS.TrashBin,
  };

  let deleteContentAction: ContextMenuItemDefinition | undefined;
  if (contextItem.type === ACL_TYPE_OWN) {
    deleteContentAction = {
      label: 'Remove from Content…',
      action: () => (dispatch: AppDispatch) => {
        dispatch(openModal({ id: ACLS_DELETE_FROM_CONTENT, contextItems }));
      },
      icon: ICONS.RemoveContent,
    };
  }

  if (aclsType && permissions['change-acl']) {
    if (
      aclsType === OBJECT_TYPE_COLLECTIONS ||
      aclsType === OBJECT_TYPE_SAVED_SEARCHES
    ) {
      items.push(editAction, editContentAction, deleteAction);
      if (deleteContentAction) {
        items.push(deleteContentAction);
      }
    } else {
      items.push(editAction, deleteAction);
    }
  }

  return {
    items,
  };
};

const AclsListContextMenu = connectContextMenuConsumer(
  contextMenuConnector(getDefinition)(ContextMenu),
  CONTEXT_MENU_ACLS_LIST,
);

export default AclsListContextMenu;
